// Audio recap mini-player store.
// Контракт MiniPlayerState из @studyhub/shared. Мы дополняем её
// `durationSec`, `queue`, `trackTitle` (UI-only).
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
	claimExclusiveAudio,
	registerExclusiveAudioStop,
	releaseExclusiveAudio,
} from "@/lib/exclusive-audio";
import { persistStorage } from "./_storage";

export interface AudioRecapQueueItem {
	recapId: string;
	title: string;
	durationSec: number;
}

/** Цикл скорости mini-player: 1x → 1.25x → 1.5x → 2x → 1x (как Speak репетитора). */
export const AUDIO_PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const;
export type AudioPlaybackRate = (typeof AUDIO_PLAYBACK_RATES)[number];

export function formatAudioPlaybackRate(rate: number): string {
	if (rate === 1) return "1x";
	if (rate === 1.25) return "1.25x";
	if (rate === 1.5) return "1.5x";
	if (rate === 2) return "2x";
	return `${rate}x`;
}

function normalizePlaybackRate(value: number): AudioPlaybackRate {
	const match = AUDIO_PLAYBACK_RATES.find((r) => r === value);
	return match ?? 1;
}

function nextPlaybackRate(current: number): AudioPlaybackRate {
	const rate = normalizePlaybackRate(current);
	const idx = AUDIO_PLAYBACK_RATES.indexOf(rate);
	return AUDIO_PLAYBACK_RATES[(idx + 1) % AUDIO_PLAYBACK_RATES.length] ?? 1;
}

interface AudioRecapState {
	recapId: string | null;
	trackTitle: string | null;
	positionSec: number;
	durationSec: number;
	/**
	 * Монотонный счётчик user-инициированных seek'ов. timeupdate из
	 * <audio> пишет positionSec напрямую (НЕ через seek()), поэтому
	 * use-mini-player проталкивает позицию в audio.currentTime только
	 * при росте seekNonce — без feedback-loop.
	 */
	seekNonce: number;
	volume: number; // 0..1
	/** Client-side playbackRate (persist). */
	speed: AudioPlaybackRate;
	isPlaying: boolean;
	queue: AudioRecapQueueItem[];
	setRecap: (item: AudioRecapQueueItem) => void;
	play: () => void;
	pause: () => void;
	toggle: () => void;
	seek: (positionSec: number) => void;
	setVolume: (volume: number) => void;
	setSpeed: (speed: number) => void;
	cycleSpeed: () => AudioPlaybackRate;
	setDuration: (durationSec: number) => void;
	enqueue: (item: AudioRecapQueueItem) => void;
	close: () => void;
}

const clamp = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));

export const useAudioRecapStore = create<AudioRecapState>()(
	persist(
		(set, get) => ({
			recapId: null,
			trackTitle: null,
			positionSec: 0,
			durationSec: 0,
			seekNonce: 0,
			volume: 1,
			speed: 1,
			isPlaying: false,
			queue: [],
			setRecap: (item) => {
				claimExclusiveAudio("audio-recap");
				set((s) => ({
					recapId: item.recapId,
					trackTitle: item.title,
					durationSec: item.durationSec,
					positionSec: 0,
					seekNonce: s.seekNonce + 1,
					isPlaying: true,
				}));
			},
			play: () => {
				claimExclusiveAudio("audio-recap");
				set({ isPlaying: true });
			},
			pause: () => {
				set({ isPlaying: false });
				releaseExclusiveAudio("audio-recap");
			},
			toggle: () => {
				if (get().isPlaying) {
					get().pause();
				} else {
					get().play();
				}
			},
			seek: (positionSec) =>
				set((s) => ({
					positionSec: clamp(positionSec, 0, s.durationSec || positionSec),
					seekNonce: s.seekNonce + 1,
				})),
			setVolume: (volume) => set({ volume: clamp(volume, 0, 1) }),
			setSpeed: (speed) => set({ speed: normalizePlaybackRate(speed) }),
			cycleSpeed: () => {
				const next = nextPlaybackRate(get().speed);
				set({ speed: next });
				return next;
			},
			setDuration: (durationSec) => set({ durationSec }),
			enqueue: (item) => set((s) => ({ queue: [...s.queue, item] })),
			close: () => {
				set({
					recapId: null,
					trackTitle: null,
					positionSec: 0,
					durationSec: 0,
					isPlaying: false,
					queue: [],
				});
				releaseExclusiveAudio("audio-recap");
			},
		}),
		{
			name: "studyhub:audio-recap:state",
			storage: persistStorage,
			// Персистим только маленькое подмножество (без queue/isPlaying).
			partialize: (state) => ({
				recapId: state.recapId,
				trackTitle: state.trackTitle,
				positionSec: state.positionSec,
				volume: state.volume,
				speed: state.speed,
			}),
			merge: (persisted, current) => {
				const p = persisted as Partial<AudioRecapState> | undefined;
				return {
					...current,
					...p,
					speed: normalizePlaybackRate(p?.speed ?? current.speed),
				};
			},
		},
	),
);

registerExclusiveAudioStop("audio-recap", () => {
	useAudioRecapStore.getState().pause();
});
